import {
  Card,
  Field,
  Flex,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Tag,
  Text,
  chakra,
  createListCollection,
  type ListCollection,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { LuCheck } from 'react-icons/lu'

import {
  DeploymentProfileSchema,
  INITIAL_PROFILE_SETTINGS_VALUES,
  WORKING_AREA_TO_COUNTRY,
  normalizeProfileSettings,
  profileSettingsToAPI,
  type ProfileSettingsData,
  type ProfileSettingsKeys,
} from '@/common/profileSettings.schema'
import {
  SALES_REGION_OPTIONS,
  parseSalesRegions,
  joinSalesRegions,
  salesRegionLabel,
} from '@/common/salesRegion'
import { getProfileSettings, updateProfileSettings } from '@/api/settings.api'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import { SettingsInput } from '../SettingsControls'
import { SettingsActionBar } from '../SettingsActionBar'
import { FormLayout } from '@/components/layout/FormLayout'

/**
 * Builds the "Ihr ausgewählter Wirkungsradius" preview text from the current
 * working area + address. The federal-state name (for FEDERAL_STATE) is derived
 * by the backend (geo_locations) and not yet exposed via the profile API, so we
 * show the place until it becomes available.
 */
function buildWirkungsradiusLabel(values: ProfileSettingsData): string {
  const place = [String(values.postal ?? '').trim(), String(values.city ?? '').trim()]
    .filter(Boolean)
    .join(' ')
  const radius = String(values.workRadius ?? '').trim()

  const areas = (values.workingArea ?? '')
    .split(',')
    .map(a => a.trim())
    .filter(Boolean)

  if (areas.length === 0) return '–'

  // Location modes are exclusive, so they are the only selected value when present.
  if (areas.includes('CITY_RADIUS')) {
    if (!place && !radius) return '–'
    return [place, radius ? `+${radius} km` : ''].filter(Boolean).join(' ')
  }
  if (areas.includes('FEDERAL_STATE')) {
    return place ? `Bundesland (${place})` : 'Bundesland'
  }

  const COUNTRY_AREA_LABELS: Record<string, string> = {
    GERMANY: 'Deutschlandweit',
    AUSTRIA: 'Österreichweit',
    SWITZERLAND: 'Schweizweit',
  }
  const labels = areas.map(a => COUNTRY_AREA_LABELS[a]).filter(Boolean)
  return labels.length > 0 ? labels.join(', ') : '–'
}

export function ProfileDataPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [initialValues, setInitialValues] = useState(INITIAL_PROFILE_SETTINGS_VALUES)
  const [openField, setOpenField] = useState<ProfileSettingsKeys | null>(null)

  // ===============================
  // LOAD PROFILE
  // ===============================
  useEffect(() => {
    let isMounted = true

    async function loadProfile() {
      try {
        setLoading(true)
        const json = await getProfileSettings()
        const normalized = normalizeProfileSettings(json)
        if (!isMounted) return
        setInitialValues({ ...INITIAL_PROFILE_SETTINGS_VALUES, ...normalized })
      } catch {
        if (!isMounted) return
        setError('Fehler beim Laden.')
        toast.error('Fehler beim Laden')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadProfile()
    return () => {
      isMounted = false
    }
  }, [])

  // ===============================
  // FORM
  // ===============================
  const formik = useFormik<ProfileSettingsData>({
    initialValues,
    validationSchema: DeploymentProfileSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async values => {
      const changed: Partial<ProfileSettingsData> = {}

      ;(Object.keys(values) as ProfileSettingsKeys[]).forEach(key => {
        const value = values[key]
        const initialValue = initialValues[key]

        const hasChanged =
          Array.isArray(value) || Array.isArray(initialValue)
            ? JSON.stringify(value ?? []) !== JSON.stringify(initialValue ?? [])
            : value !== initialValue

        if (hasChanged) {
          ;(changed as Record<ProfileSettingsKeys, unknown>)[key] = value
        }
      })

      if (Object.keys(changed).length === 0) {
        toast.info('Keine Änderungen gefunden')
        return
      }

      // Land must always be persisted as a country code: in the country-only working
      // areas it is auto-set (and hidden) and often matches the initial value, so the
      // diff above would otherwise drop it and the BE would receive no country.
      if (values.country && changed.country === undefined) {
        changed.country = values.country
      }

      try {
        setSaving(true)
        const apiPayload = profileSettingsToAPI(changed)
        await updateProfileSettings(apiPayload)
        toast.success('Änderungen gespeichert')
        const updated = normalizeProfileSettings({ ...initialValues, ...changed })
        setInitialValues(updated)
        formik.resetForm({ values: updated })
      } catch (err: any) {
        if (err?.code === 'INVALID_PAYLOAD' && err?.errors) {
          const backendErrors: Record<string, string> = {}
          Object.keys(err.errors).forEach(key => {
            const messages = err.errors[key]
            if (Array.isArray(messages) && messages.length > 0) {
              backendErrors[key] = messages[0]
            }
          })
          formik.setErrors(backendErrors)
          Object.keys(backendErrors).forEach(key => formik.setFieldTouched(key, true))
          toast.error('Validierungsfehler', 'Bitte überprüfen Sie die markierten Felder')
        } else {
          toast.error(err?.message || 'Fehler beim Speichern')
        }
      } finally {
        setSaving(false)
      }
    },
  })

  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(
    formik.dirty
  )
  const [cancelOpen, setCancelOpen] = useState(false)

  const showActionBar = formik.dirty
  const saveDisabled = !formik.dirty || !formik.isValid || saving

  function resetToInitial() {
    formik.resetForm({ values: initialValues })
    formik.setErrors({})
  }
  async function doSave() {
    await formik.submitForm()
  }

  // ===============================
  // COLLECTIONS
  // ===============================
  const areaCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'CITY_RADIUS', label: 'Stadt + Radius (km)' },
          { value: 'FEDERAL_STATE', label: 'Bundesland' },
          { value: 'GERMANY', label: 'Deutschland' },
          { value: 'AUSTRIA', label: 'Österreich' },
          { value: 'SWITZERLAND', label: 'Schweiz' },
        ],
      }),
    []
  )

  const countryCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'Deutschland', label: 'Deutschland' },
          { value: 'Österreich', label: 'Österreich' },
          { value: 'Schweiz', label: 'Schweiz' },
        ],
      }),
    []
  )

  const topicCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'RETIREMENT_PROVISION', label: 'Altersvorsorge' },
          { value: 'VERMOEGEN_PLUS', label: 'VermögenPlus' },
          { value: 'MEIN_INVEST', label: 'MeinInvest' },
          { value: 'UNION_DEPOT_KOMFORT', label: 'UnionDepot Komfort' },
          { value: 'WERT_ANLAGE', label: 'WertAnlage' },
          { value: 'ELTIF', label: 'Eltif' },
          { value: 'WEALTH_STRUCTURING', label: 'Vermögensstrukturierung' },
          { value: 'CUSTOMER_SERVICE_CENTER', label: 'Begleitung im Kundenservicecenter' },
          { value: 'PREMIUM_SEGMENT', label: 'Begleitung im gehobenen Segment' },
          { value: 'BANKING_WORKSPACE', label: 'Banking Workspace' },
          { value: 'FUND_LINKED_PENSION', label: 'Fondsgebundene Rentenversicherung' },
        ],
      }),
    []
  )

  const salesRegionCollection = useMemo(
    () => createListCollection({ items: SALES_REGION_OPTIONS }),
    []
  )

  // ===============================
  // SELECT COMPONENT
  // ===============================
  function FormikSelect({
    field,
    collection,
    placeholder = 'Bitte auswählen',
  }: {
    field: ProfileSettingsKeys
    collection: ListCollection<{ value: string; label: string }>
    placeholder?: string
  }) {
    const error = formik.touched[field] && formik.errors[field]
    const currentValue = formik.values[field]
    const isTopics = field === 'topics'

    const selectValue = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : []

    return (
      <Select.Root
        collection={collection}
        value={selectValue}
        multiple={isTopics}
        onValueChange={({ value }) => {
          // setFieldValue already revalidates with the new value; validating again on
          // setFieldTouched would race against stale Formik state.
          formik.setFieldValue(field, isTopics ? value : value[0])
          formik.setFieldTouched(field, true, false)
          if (!isTopics) setOpenField(null)
        }}
        positioning={{
          strategy: 'fixed',
          placement: 'bottom-start',
          sameWidth: true,
          offset: { mainAxis: 6, crossAxis: 0 },
        }}
        open={openField === field}
        onOpenChange={e => setOpenField(e.open ? field : null)}
        invalid={!!error}
      >
        <Select.Trigger w="full">
          {isTopics ? (
            // Topics trigger always shows the placeholder; selections appear as chips below.
            <Text color="fg.muted">{placeholder}</Text>
          ) : (
            <Select.ValueText placeholder={placeholder} />
          )}
          <Select.Indicator />
        </Select.Trigger>

        <Select.Positioner>
          <Select.Content w="var(--reference-width)" maxH="260px" overflowY="auto">
            {collection.items.map(item => {
              const selected = selectValue.includes(item.value)
              return (
                <Select.Item key={item.value} item={item.value}>
                  <HStack justify="space-between" w="full">
                    <Text>{item.label}</Text>
                    {isTopics && selected && <Text>✓</Text>}
                  </HStack>
                </Select.Item>
              )
            })}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    )
  }

  // ===============================
  // DERIVED
  // ===============================
  // workingArea is a comma-separated list — the country areas can be multi-selected,
  // while the location modes (CITY_RADIUS / FEDERAL_STATE) stay exclusive single picks.
  const selectedAreas = (formik.values.workingArea ?? '')
    .split(',')
    .map(a => a.trim())
    .filter(Boolean)
  const isCityRadius = selectedAreas.includes('CITY_RADIUS')
  const isFederalState = selectedAreas.includes('FEDERAL_STATE')
  const isCountryMode = selectedAreas.some(a => !!WORKING_AREA_TO_COUNTRY[a])
  // Postal/city/country are required (and marked) for the location-based areas.
  const locationRequired = isCityRadius || isFederalState
  const areaLabel = (value: string) =>
    areaCollection.items.find(i => i.value === value)?.label ?? value

  // The Bereich select is multi-select for the country areas, while the location modes
  // (CITY_RADIUS / FEDERAL_STATE) are exclusive: picking one clears any countries, and
  // picking a country clears any location mode. Switching also clears the fields that
  // become hidden and auto-sets the country/countries. Updating everything via setValues
  // validates once against the complete new state, so a previously-required field (e.g.
  // radius) can no longer linger as "required" after switching away from it.
  const LOCATION_MODES = ['CITY_RADIUS', 'FEDERAL_STATE']

  function handleWorkingAreaChange(nextAreas: string[]) {
    const prev = (formik.values.workingArea ?? '').split(',').filter(Boolean)
    const added = nextAreas.filter(v => !prev.includes(v))

    let resolved: string[]
    if (added.some(v => LOCATION_MODES.includes(v))) {
      // A location mode was just added → it becomes the sole selection.
      resolved = [added.find(v => LOCATION_MODES.includes(v)) as string]
    } else {
      // Countries are multi-select; drop any previously selected location mode.
      resolved = nextAreas.filter(v => !LOCATION_MODES.includes(v))
    }

    const next: ProfileSettingsData = { ...formik.values, workingArea: resolved.join(',') }
    const countries = resolved.filter(a => WORKING_AREA_TO_COUNTRY[a])

    if (resolved.includes('CITY_RADIUS') || resolved.includes('FEDERAL_STATE')) {
      if (resolved.includes('FEDERAL_STATE')) next.workRadius = ''
      // Collapse any leftover multi-country value to a single Land for the
      // location-based modes, where Land is a single required field again.
      next.country = (next.country ?? '').split(',')[0] ?? ''
    } else if (countries.length > 0) {
      // Country areas: keep Bereich + clear the hidden location fields. The full
      // multi-country scope lives in workingArea; the save endpoint accepts only a
      // single country code, so Land mirrors the first selected country.
      // salesRegion stays visible for every area, so it is preserved.
      next.workRadius = ''
      next.postal = ''
      next.city = ''
      next.country = WORKING_AREA_TO_COUNTRY[countries[0]]
    }
    // Empty selection: keep the current values.

    formik.setValues(next, true)
    formik.setFieldTouched('workingArea', true, false)
    // Keep the dropdown open so multiple countries can be picked in one go (it closes
    // on outside click). Matches the topics / Vertriebsregion multi-selects.
  }

  const RequiredStar = () => <Field.RequiredIndicator />

  const selectedTopics: string[] = Array.isArray(formik.values.topics) ? formik.values.topics : []
  const topicLabel = (code: string) =>
    topicCollection.items.find(i => i.value === code)?.label ?? code

  function removeTopic(code: string) {
    formik.setFieldValue(
      'topics',
      selectedTopics.filter(c => c !== code)
    )
    formik.setFieldTouched('topics', true, false)
  }

  // salesRegion is stored as a comma-joined string; the multi-select works with
  // the parsed array and writes the joined string back.
  const selectedSalesRegions: string[] = parseSalesRegions(formik.values.salesRegion)

  function setSalesRegions(values: string[]) {
    formik.setFieldValue('salesRegion', joinSalesRegions(values))
    formik.setFieldTouched('salesRegion', true, false)
  }

  function removeSalesRegion(value: string) {
    setSalesRegions(selectedSalesRegions.filter(v => v !== value))
  }

  // ===============================
  // LOADING / ERROR
  // ===============================
  if (loading) {
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Spinner />
      </Flex>
    )
  }
  if (error) {
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Text color="fg.error">{error}</Text>
      </Flex>
    )
  }

  // ===============================
  // RENDER
  // ===============================
  return (
    <>
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={confirmNavigation}
        onSave={async () => {
          if (formik.dirty) await formik.submitForm()
          confirmNavigation()
        }}
        onNavigate={cancelNavigation}
      />
      <DismissChangesDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onDismiss={() => {
          resetToInitial()
          setCancelOpen(false)
        }}
        onSave={async () => {
          await doSave()
          setCancelOpen(false)
        }}
        onNavigate={() => {
          // kept for API compatibility
        }}
      />

      <chakra.form data-testid="profile-data" onSubmit={formik.handleSubmit}>
        <fieldset disabled={saving} style={{ width: '100%' }}>
          <FormLayout.Root>
            <FormLayout.Section>
              <FormLayout.Title>Profildaten verwalten</FormLayout.Title>
              <FormLayout.Description>
                Füllen Sie Ihr Profil als SIB mit Wirkungsradius und Themen.
              </FormLayout.Description>
            </FormLayout.Section>

            {/* SECTION: WIRKUNGSRADIUS */}
            <Flex direction="column" gap={4}>
              <FormLayout.Title>Wirkungsradius</FormLayout.Title>

              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                <Field.Root invalid={!!(formik.touched.workingArea && formik.errors.workingArea)}>
                  <Field.Label>Bereich</Field.Label>
                  <Select.Root
                    collection={areaCollection}
                    value={selectedAreas}
                    multiple
                    onValueChange={({ value }) => handleWorkingAreaChange(value)}
                    positioning={{
                      strategy: 'fixed',
                      placement: 'bottom-start',
                      sameWidth: true,
                      offset: { mainAxis: 6, crossAxis: 0 },
                    }}
                    open={openField === 'workingArea'}
                    onOpenChange={e => setOpenField(e.open ? 'workingArea' : null)}
                    invalid={!!(formik.touched.workingArea && formik.errors.workingArea)}
                  >
                    <Select.Trigger w="full">
                      {selectedAreas.length > 0 ? (
                        <Text>{selectedAreas.map(areaLabel).join(', ')}</Text>
                      ) : (
                        <Text color="fg.muted">Bitte auswählen</Text>
                      )}
                      <Select.Indicator />
                    </Select.Trigger>

                    <Select.Positioner>
                      <Select.Content w="var(--reference-width)" maxH="260px" overflowY="auto">
                        {areaCollection.items.map(item => {
                          const selected = selectedAreas.includes(item.value)
                          return (
                            <Select.Item key={item.value} item={item.value}>
                              <HStack justify="space-between" w="full">
                                <Text>{item.label}</Text>
                                {selected && <Text>✓</Text>}
                              </HStack>
                            </Select.Item>
                          )
                        })}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  {formik.touched.workingArea && formik.errors.workingArea && (
                    <Field.ErrorText>{formik.errors.workingArea}</Field.ErrorText>
                  )}
                </Field.Root>

                {isCityRadius && (
                  <Field.Root invalid={!!(formik.touched.workRadius && formik.errors.workRadius)}>
                    <Field.Label>
                      Radius (km) <RequiredStar />
                    </Field.Label>
                    <SettingsInput
                      {...formik.getFieldProps('workRadius')}
                      inputMode="numeric"
                      onChange={e => {
                        const sanitized = e.target.value.replace(/\D/g, '')
                        formik.setFieldValue('workRadius', sanitized)
                        if (!formik.touched.workRadius) {
                          formik.setFieldTouched('workRadius', true, false)
                        }
                      }}
                    />
                    {formik.touched.workRadius && formik.errors.workRadius && (
                      <Field.ErrorText>{formik.errors.workRadius}</Field.ErrorText>
                    )}
                  </Field.Root>
                )}

                {/* Location fields are hidden entirely for the country-only working areas. */}
                {!isCountryMode && (
                  <>
                    <Field.Root invalid={!!(formik.touched.postal && formik.errors.postal)}>
                      <Field.Label>
                        Postleitzahl {locationRequired && <RequiredStar />}
                      </Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('postal')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.postal) {
                            formik.setFieldTouched('postal', true, false)
                          }
                        }}
                      />
                      {formik.touched.postal && formik.errors.postal && (
                        <Field.ErrorText>{formik.errors.postal}</Field.ErrorText>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!(formik.touched.city && formik.errors.city)}>
                      <Field.Label>Stadt {locationRequired && <RequiredStar />}</Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('city')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.city) {
                            formik.setFieldTouched('city', true, false)
                          }
                        }}
                      />
                      {formik.touched.city && formik.errors.city && (
                        <Field.ErrorText>{formik.errors.city}</Field.ErrorText>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!(formik.touched.country && formik.errors.country)}>
                      <Field.Label>Land {locationRequired && <RequiredStar />}</Field.Label>
                      <FormikSelect field="country" collection={countryCollection} />
                      {formik.touched.country && formik.errors.country && (
                        <Field.ErrorText>{formik.errors.country}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </>
                )}

                {/* Vertriebsregion stays visible for every Bereich selection. */}
                <Field.Root invalid={!!(formik.touched.salesRegion && formik.errors.salesRegion)}>
                  <Field.Label>Vertriebsregion</Field.Label>
                  <Select.Root
                    collection={salesRegionCollection}
                    value={selectedSalesRegions}
                    multiple
                    onValueChange={({ value }) => setSalesRegions(value)}
                    positioning={{
                      strategy: 'fixed',
                      placement: 'bottom-start',
                      sameWidth: true,
                      offset: { mainAxis: 6, crossAxis: 0 },
                    }}
                    open={openField === 'salesRegion'}
                    onOpenChange={e => setOpenField(e.open ? 'salesRegion' : null)}
                    invalid={!!(formik.touched.salesRegion && formik.errors.salesRegion)}
                  >
                    <Select.Trigger w="full">
                      <Text color="fg.muted">Bitte auswählen</Text>
                      <Select.Indicator />
                    </Select.Trigger>

                    <Select.Positioner>
                      <Select.Content w="var(--reference-width)" maxH="260px" overflowY="auto">
                        {salesRegionCollection.items.map(item => {
                          const selected = selectedSalesRegions.includes(item.value)
                          return (
                            <Select.Item key={item.value} item={item.value}>
                              <HStack justify="space-between" w="full">
                                <Text>{item.label}</Text>
                                {selected && <Text>✓</Text>}
                              </HStack>
                            </Select.Item>
                          )
                        })}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.HelperText>
                    Sie können mehrere Vertriebsregionen auswählen.
                  </Field.HelperText>
                  {formik.touched.salesRegion && formik.errors.salesRegion && (
                    <Field.ErrorText>{formik.errors.salesRegion}</Field.ErrorText>
                  )}
                </Field.Root>

                {selectedSalesRegions.length > 0 && (
                  <Flex wrap="wrap" gap={2} mt={1}>
                    {selectedSalesRegions.map(value => (
                      <Tag.Root key={value} variant="subtle">
                        <Tag.Label>{salesRegionLabel(value)}</Tag.Label>
                        <Tag.CloseTrigger
                          aria-label={`${salesRegionLabel(value)} entfernen`}
                          onClick={() => removeSalesRegion(value)}
                        />
                      </Tag.Root>
                    ))}
                  </Flex>
                )}
              </SimpleGrid>

              {/* PREVIEW */}
              <Card.Root maxW={{ base: 'full', sm: '50%' }}>
                <Card.Body>
                  <Card.Title>Ihr ausgewählter Wirkungsradius</Card.Title>
                  <Card.Description>{buildWirkungsradiusLabel(formik.values)}</Card.Description>
                </Card.Body>
              </Card.Root>
            </Flex>

            {/* SECTION: THEMENAUSWAHL */}
            <Flex direction="column" gap={4}>
              <FormLayout.Title>Themen von Interesse</FormLayout.Title>
              <FormLayout.Description>
                Wählen Sie die Themen aus, die für Sie relevant sind. Sie können mehrere Themen
                auswählen. Wenn Sie keine Auswahl treffen, werden alle Themen berücksichtigt.
              </FormLayout.Description>

              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                <Field.Root invalid={!!(formik.touched.topics && formik.errors.topics)}>
                  <Field.Label>Themen auswählen</Field.Label>
                  <FormikSelect
                    field="topics"
                    collection={topicCollection}
                    placeholder="Bitte Themen auswählen"
                  />
                  {formik.touched.topics && typeof formik.errors.topics === 'string' && (
                    <Field.ErrorText>{formik.errors.topics}</Field.ErrorText>
                  )}
                </Field.Root>
              </SimpleGrid>

              {selectedTopics.length > 0 && (
                <Flex direction="column" gap={2}>
                  <Text>
                    {selectedTopics.length} {selectedTopics.length === 1 ? 'Thema' : 'Themen'}{' '}
                    ausgewählt
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {selectedTopics.map(code => (
                      <Tag.Root key={code} variant="subtle" colorPalette="green">
                        <Tag.StartElement>
                          <LuCheck />
                        </Tag.StartElement>
                        <Tag.Label>{topicLabel(code)}</Tag.Label>
                        <Tag.CloseTrigger
                          aria-label={`${topicLabel(code)} entfernen`}
                          onClick={() => removeTopic(code)}
                        />
                      </Tag.Root>
                    ))}
                  </Flex>
                </Flex>
              )}
            </Flex>
          </FormLayout.Root>
        </fieldset>
      </chakra.form>

      <SettingsActionBar
        visible={showActionBar}
        onCancel={() => setCancelOpen(true)}
        onSave={doSave}
        saveDisabled={saveDisabled}
        saving={saving}
      />
    </>
  )
}
